<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <div>
        <b-row>
          <b-col md="4">
            <e-store-combo
              id="filters-store"
              v-model="filters.storeId"
              :required="false"
              :placeholder="$t('Todos')"
              @input="storeChanged()"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="order-paybox"
              v-model="filters.payboxId"
              type="vue-select"
              :label="$t('PDV')"
              :placeholder="$t('Selecione')"
              :options="payBoxesOptions"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="filters-command"
              v-model="filters.command"
              type="vue-select"
              :label="$t('Comando')"
              :placeholder="$t('Selecione')"
              :options="payBoxCommandsOptions()"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="filters-status-command"
              v-model="filters.statusCommand"
              type="vue-select"
              :label="$t('Status')"
              :placeholder="$t('Selecione')"
              :options="statusCommandOptions"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="period"
              v-model="filters.rangeDate"
              type="date-range-picker"
              :label="$t('Período')"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="platform"
              v-model="filters.platform"
              type="vue-select"
              :label="$t('Sistema Operacional')"
              :placeholder="$t('Selecione')"
              :options="platformOptions"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <b-card-actions
      class="mb-2"
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>
      <b-table
        ref="command-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($tc('PAYBOX_COMMAND.NAME'), 'male', queryExecuted)"
        :fields="fields"
        :items="commands"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(payBoxId)="{ item }">
          {{ item.payBox.number }}-{{ item.payBox.description }}
        </template>
        <template #cell(commandResponse)="{ item }">
          <pre class="content-control">{{ formatJson(item.commandResponse) }}</pre>
          <e-grid-actions
            :show-update="false"
            :show-delete="false"
            :buttons="[
              {
                icon: 'arrows-fullscreen',
                variant: 'outline-primary',
                title: $t('Visualizar resposta'),
                event: 'response-read-only',
              },
              {
                icon: 'clipboard',
                variant: 'outline-primary',
                title: $t('Copiar'),
                event: 'response-copy',
              },
            ]"
            @response-read-only="showCommandResponseModal(item.commandResponse)"
            @response-copy="copyToClipboard(item.commandResponse)"
          />
        </template>
      </b-table>

      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="table-manage-pay-box"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <manage-pay-box-commands-sidebar
      ref="managePayBoxCommandsSidebar"
      @after-save="getData"
    />

    <fab
      v-if="$can('Create', 'PayBoxCommand')"
      :main-tooltip="$t('Cadastrar novo Comando')"
      @click="onShowSidebar"
    />
    <b-modal
      id="commandResponseModal1"
      v-model="showModal"
      no-close-on-backdrop
      hide-footer
      size="xl"
      centered
      :title="$t('Resposta')"
    >
      <pre> {{ commandResponse }} </pre>

      <b-row class="w-100 d-flex justify-content-between align-items-center">
        <b-col class="d-flex justify-content-end">
          <e-button
            class="mr-1"
            variant="outline-primary"
            :text="$t('Voltar')"
            :text-shortcuts="['ESC']"
            @click="showModal = false"
          />
          <e-button
            variant="primary"
            :text="$t('Copiar')"
            @click="copyToClipboard(commandResponse)"
          />
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { EPageSizeSelector, EPaginationSummary, EGridActions } from '@/views/components'
import fab from '@/views/components/FAB.vue'
import { translate, payBoxCommandDomain } from '@/mixins'
import EFilters from '@/views/components/EFilters.vue'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import EButton from '@/views/components/EButton.vue'
import ManagePayBoxCommandsSidebar from './ManagePayBoxCommandsSidebar.vue'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    EGridActions,
    EButton,
    fab,
    EFilters,
    EStoreCombo,
    ManagePayBoxCommandsSidebar,
  },

  mixins: [translate, payBoxCommandDomain],

  data() {
    return {
      queryExecuted: false,
      fetching: false,
      showModal: false,
      commandResponse: '',
    }
  },

  computed: {
    ...mapGetters('pages/payboxCommands', ['payBoxesOptions', 'statusCommandOptions']),
    ...mapGetters('pages/payboxCommands/managePayBoxCommands', ['platformOptions']),
    ...mapState('pages/payboxCommands', {
      payBoxes: 'payBoxes',
      paging: 'paging',
      sorting: 'sorting',
      filters: 'filters',
      commands: 'commands',
    }),

    buttons() {
      return [
        {
          icon: 'journal-code',
          event: 'request-logs',
          title: this.$t('Solicitar logs'),
        },
      ]
    },

    fields() {
      return [
        {
          label: this.$t('Código'),
          key: 'id',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
        },
        {
          label: this.$t('Loja'),
          key: 'payBox.store.id',
          thClass: 'text-center',
          tdClass: 'text-left',
          formatter: (val, index, item) => this.$options.filters.storeName(item.payBox.store),
          sortable: true,
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Pdv'),
          key: 'payBoxId',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
        },
        {
          label: this.$t('Comando'),
          key: 'command',
          tdClass: 'text-left',
          thStyle: { width: '100px' },
          formatter: value => this.payBoxCommandsLabel[value],
        },
        {
          label: this.$t('Resposta'),
          key: 'commandResponse',
          tdClass: 'text-left',
          thStyle: { width: '100px' },
          formatter: value => this.$t(value),
        },
        {
          label: this.$t('Usuário Logado'),
          key: 'loggedUser.name',
          tdClass: 'text-left',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Status'),
          key: 'status',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
        },
        {
          label: this.$t('Data Solicitação'),
          key: 'sendingDate',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
          formatter: value => this.$options.filters.datetime(value),
        },
        {
          label: this.$t('Data Execução'),
          key: 'executedDate',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
          formatter: value => this.$options.filters.datetime(value),
        },
        {
          label: this.$t('Informações Adicionais'),
          key: 'additionalInformation',
          tdClass: 'text-left',
          thStyle: { width: '100px' },
          formatter: value => this.$t(value),
        },
        {
          label: this.$t('SO'),
          key: 'platform',
          tdClass: 'text-left',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Broadcast'),
          key: 'broadcast',
          tdClass: 'text-left',
          thStyle: { width: '100px' },
        },
      ]
    },
  },

  mounted() {},

  methods: {
    ...mapActions('pages/payboxCommands', [
      'fetchPayBoxes',
      'fetchCommands',
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
    ]),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchCommands()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    formatJson(item) {
      const value = typeof item !== 'string' ? JSON.stringify(item) : item
      try {
        return JSON.parse(value)
      } catch (e) {
        return value
      }
    },

    async showCommandResponseModal(response) {
      this.commandResponse = this.formatJson(response)
      this.showModal = true
    },

    async copyToClipboard(response) {
      const formatted =
        typeof response === 'string'
          ? JSON.stringify(this.formatJson(response), null, 2)
          : JSON.stringify(response, null, 2)
      if (navigator.clipboard && response) {
        await navigator.clipboard
          .writeText(formatted)
          .then(() => {
            this.showSuccess({ message: this.$t('Copiado para a área de transferência!') })
          })
          .catch(error => {
            this.showError({ error })
          })
      }
    },
    storeChanged() {
      if (this.filters.storeId) {
        this.$nextTick(async () => {
          try {
            this.fetching = true
            await this.fetchPayBoxes(this.filters.storeId)
          } catch (error) {
            this.showError({ error })
          } finally {
            this.fetching = false
          }
        })
      }
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.getData()
    },
    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    onShowSidebar() {
      this.$refs.managePayBoxCommandsSidebar.showCreate()
    },
  },
}
</script>

<style lang="scss" scoped>
.content-control {
  width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
